import { Children, type ReactNode, useContext } from 'react'

import { HelpCenterContext } from '@/components/HtmlArticle/HelpCenterContext'

import { Text } from '@/components/Text/Text'
import { Paragraph } from '../Paragraph/Paragraph'

import css from './note.module.css'
import { ConditionalWrap } from '@/components/conditional-wrap/conditional-wrap'
import {
    type HelpCenterNoteProps,
    HelpCenterNote,
} from '@/components/help-center-note/help-center-note'
import { LightBulbBase, InfoCircle, Warning } from '@doist/icons/brand'

export type NoteProps = HelpCenterNoteProps & {
    name?: string
    description?: string
    badge?: ReactNode
}

function TipFromTitle({ name, description }: { name?: string; description?: string }) {
    return (
        <Text className={css.title} size="minor-base">
            <strong>{name}</strong>
            {description && ` · ${description}`}
        </Text>
    )
}

function Title({ children }: { children: ReactNode }) {
    return (
        <Text className={css.title} size="minor-base" weight="semibold">
            {children}
        </Text>
    )
}

function wrapper(children: React.ReactNode) {
    return <Paragraph>{children}</Paragraph>
}

export function Note({ variant, children, ...rest }: NoteProps) {
    const { labels } = useContext(HelpCenterContext)
    let title: ReactNode
    let Icon: ReactNode
    switch (variant) {
        case 'tip': {
            const { name, description } = rest

            if (name) {
                title = <TipFromTitle name={name} description={description} />
            } else {
                title = <Title>{labels.help.articleBoxes.quickTip}</Title>
            }

            Icon = <LightBulbBase />
            break
        }
        case 'note':
            title = <Title>{labels.help.articleBoxes.note}</Title>
            Icon = <InfoCircle />
            break
        case 'warning':
            title = <Title>{labels.help.articleBoxes.warning}</Title>
            Icon = <Warning />
            break
    }

    const [firstChild] = Children.toArray(children)
    const isFirstChildText = typeof firstChild === 'string'
    return (
        <HelpCenterNote className={css.note} title={title} variant={variant} icon={Icon} {...rest}>
            <ConditionalWrap condition={isFirstChildText} wrap={wrapper}>
                {children}
            </ConditionalWrap>
        </HelpCenterNote>
    )
}
